// 🏦 은행 잔고 서비스 - Firestore CRUD

import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from './firebase';
import { storage } from './firebase';
import { getAuth } from 'firebase/auth';
import type { BankBalance, BankBalanceFormData, CashReserve } from '../types/bankBalance';

// 컬렉션 참조
const bankBalanceRef = collection(db, 'bank-balances');
const cashReserveRef = collection(db, 'cash-reserves');

// 스크린샷 업로드 함수
async function uploadScreenshot(file: File, balanceId: string): Promise<string> {
    const fileExtension = file.name.split('.').pop() || 'png';
    const fileName = `bank-screenshots/${balanceId}_${Date.now()}.${fileExtension}`;
    const storageRef = ref(storage, fileName);

    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);
    return downloadUrl;
}

/**
 * 은행 잔고 서비스
 */
export const bankBalanceService = {
    // =====================
    // 은행 잔고 (HUF)
    // =====================

    // 은행 잔고 기록 생성
    async createBankBalance(formData: BankBalanceFormData, calculatedBalance: number): Promise<string> {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            throw new Error('로그인이 필요합니다.');
        }

        const difference = formData.bankBalance - calculatedBalance;

        // 먼저 문서 생성 (스크린샷 URL 없이)
        const docData: Record<string, any> = {
            date: formData.date,
            bankBalance: formData.bankBalance,
            accountName: formData.accountName || 'OTP Bank',
            calculatedBalance,
            difference,
            differenceExplanation: formData.differenceExplanation || '',
            isVerified: false,
            notes: formData.notes || '',
            createdBy: user.email,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(bankBalanceRef, docData);

        // 스크린샷 파일이 있으면 업로드 후 URL 업데이트
        if (formData.screenshotFile) {
            const screenshotUrl = await uploadScreenshot(formData.screenshotFile, docRef.id);
            await updateDoc(docRef, { screenshotUrl });
        }

        return docRef.id;
    },

    // 특정 날짜의 은행 잔고 조회
    async getBankBalanceByDate(date: string): Promise<BankBalance | null> {
        const q = query(bankBalanceRef, where('date', '==', date));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return null;
        }

        const doc = snapshot.docs[0];
        return {
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            verifiedAt: doc.data().verifiedAt?.toDate(),
        } as BankBalance;
    },

    // 최근 은행 잔고 조회
    async getRecentBankBalances(count: number = 12): Promise<BankBalance[]> {
        const q = query(
            bankBalanceRef,
            orderBy('date', 'desc'),
            limit(count)
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            verifiedAt: doc.data().verifiedAt?.toDate(),
        } as BankBalance));
    },

    // 특정 연도의 은행 잔고 조회
    async getBankBalancesByYear(year: number): Promise<BankBalance[]> {
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;

        const q = query(
            bankBalanceRef,
            where('date', '>=', startDate),
            where('date', '<=', endDate),
            orderBy('date', 'desc')
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            verifiedAt: doc.data().verifiedAt?.toDate(),
        } as BankBalance));
    },

    // 은행 잔고 검증 확인
    async verifyBankBalance(id: string): Promise<void> {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            throw new Error('로그인이 필요합니다.');
        }

        const docRef = doc(bankBalanceRef, id);
        await updateDoc(docRef, {
            isVerified: true,
            verifiedAt: serverTimestamp(),
            verifiedBy: user.email,
            updatedAt: serverTimestamp(),
        });
    },

    // 은행 잔고 수정
    async updateBankBalance(id: string, formData: Partial<BankBalanceFormData>): Promise<void> {
        const docRef = doc(bankBalanceRef, id);
        await updateDoc(docRef, {
            ...formData,
            updatedAt: serverTimestamp(),
        });
    },

    // 은행 잔고 삭제
    async deleteBankBalance(id: string): Promise<void> {
        const docRef = doc(bankBalanceRef, id);
        await deleteDoc(docRef);
    },

    // =====================
    // 현금 보관 (EUR, USD, KRW)
    // =====================

    // 현금 보관 기록 생성
    async createCashReserve(
        date: string,
        currency: 'EUR' | 'USD' | 'KRW',
        amount: number,
        location?: string,
        notes?: string
    ): Promise<string> {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            throw new Error('로그인이 필요합니다.');
        }

        const docData = {
            date,
            currency,
            amount,
            location: location || '',
            notes: notes || '',
            createdBy: user.email,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(cashReserveRef, docData);
        return docRef.id;
    },

    // 최근 현금 보관 조회 (통화별)
    async getRecentCashReserves(currency: 'EUR' | 'USD' | 'KRW'): Promise<CashReserve[]> {
        const q = query(
            cashReserveRef,
            where('currency', '==', currency),
            orderBy('date', 'desc'),
            limit(12)
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
        } as CashReserve));
    },

    // 현금 보관 삭제
    async deleteCashReserve(id: string): Promise<void> {
        const docRef = doc(cashReserveRef, id);
        await deleteDoc(docRef);
    },
};
